// DashboardG.tsx - IBIRACO - Painel Administrativo (MULTI-CIDADES + COMPRESSÃO)
import React, { useEffect, useState } from 'react';
import { db, storage, auth } from './firebaseConfig';
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  MapPin, Construction, CheckCircle, Clock, Star, ThumbsUp, Camera, User,
  TrendingUp, AlertTriangle, LogOut, ArrowLeft, Award, Megaphone, MapIcon, Activity
} from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { useNavigate } from 'react-router-dom';

export default function DashboardG() {
  const navigate = useNavigate();
  const [reportes, setReportes] = useState<any[]>([]);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  
  // Estados da cidade
  const [cidadeDisplay, setCidadeDisplay] = useState<string>('');
  const [cidadeNormalizada, setCidadeNormalizada] = useState<string>('');
  const [loading, setLoading] = useState(true);
  
  // Métricas
  const [stats, setStats] = useState({
    pendentes: 0,
    emObra: 0,
    resolvidos: 0,
    totalReportes: 0,
    taxaResolucao: 0
  });

  // ============================================================================
  // 🔄 INICIALIZAÇÃO - Buscar cidade do admin
  // ============================================================================
  
  useEffect(() => {
    const cidadeDisplayLS = localStorage.getItem('cidadeDisplay');
    const cidadeNormalizadaLS = localStorage.getItem('cidadeNormalizada');
    
    if (cidadeDisplayLS && cidadeNormalizadaLS) {
      setCidadeDisplay(cidadeDisplayLS);
      setCidadeNormalizada(cidadeNormalizadaLS);
      setLoading(false);
    } else {
      const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
          const email = user.email || '';
          if (email.includes('mariocampos')) {
            setCidadeDisplay('Mário Campos');
            setCidadeNormalizada('mario_campos');
          } else if (email.includes('sarzedo')) {
            setCidadeDisplay('Sarzedo');
            setCidadeNormalizada('sarzedo');
          } else {
            setCidadeDisplay('Ibirité');
            setCidadeNormalizada('ibirite');
          }
          setLoading(false);
        }
      });
      return () => unsubscribe();
    }
  }, []);

  // ============================================================================
  // 🔄 MONITORAR REPORTES FILTRADOS POR CIDADE
  // ============================================================================
  
  useEffect(() => {
    if (!cidadeNormalizada) return;

    console.log('🔍 Filtrando reportes para cidadeReporte:', cidadeNormalizada);

    // ✅ Filtro duplo apenas para Ibirité (aceita "ibirite" e "Ibirité")
    const cidadesBusca = cidadeNormalizada === "ibirite"
      ? ["ibirite", "Ibirité"]
      : [cidadeNormalizada];

    const q = query(
      collection(db, "reportes"),
      where("cidadeReporte", "in", cidadesBusca),
      orderBy("createdAt", "desc")
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('📊 Reportes encontrados:', snapshot.size);
      
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }) as any);
      
      // Calcula métricas
      const pendentes = data.filter((r: any) => r.status === 'Pendente').length;
      const emObra = data.filter((r: any) => r.status === 'Em obra').length;
      const resolvidos = data.filter((r: any) => r.status === 'Resolvido').length;
      const total = data.length;
      
      setStats({
        pendentes,
        emObra,
        resolvidos,
        totalReportes: total,
        taxaResolucao: total > 0 ? Math.round((resolvidos / total) * 100) : 0
      });
      
      setReportes(data);
    });
    
    return () => unsubscribe();
  }, [cidadeNormalizada]);

  // ============================================================================
  // 🎯 FUNÇÕES
  // ============================================================================

  const alterarStatus = async (id: string, novoStatus: string) => {
    try {
      await updateDoc(doc(db, "reportes", id), { status: novoStatus });
    } catch (e) {
      alert("Erro ao atualizar status.");
    }
  };

  // ✅ UPLOAD COM COMPRESSÃO DE IMAGEM
  const uploadResolvido = async (id: string, file: File) => {
    setUploadingId(id);
    try {
      const options = {
        maxSizeMB: 0.5,           // Limite de 500KB
        maxWidthOrHeight: 1920,   // Redimensiona se necessário
        useWebWorker: true,       // Não trava a UI
        fileType: 'image/jpeg',   // Converte para JPEG
        initialQuality: 0.8       // 80% de qualidade
      };
      
      const compressedFile = await imageCompression(file, options);
      
      const storageRef = ref(storage, `solucoes/${id}_final.jpg`);
      await uploadBytes(storageRef, compressedFile);
      const url = await getDownloadURL(storageRef);

      await updateDoc(doc(db, "reportes", id), {
        status: 'Resolvido',
        imagemSolucao: url,
        resolvidoAt: new Date(),
        imageSizeKB: Math.round(compressedFile.size / 1024) // Tamanho em KB
      });
      
      alert("Foto da solução enviada com sucesso! ");
    } catch (e) {
      console.error(e);
      alert("Erro ao processar imagem.");
    } finally {
      setUploadingId(null);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // ============================================================================
  // 🎨 RENDERIZAÇÃO
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f9f9fb] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#4800b2] mx-auto mb-4"></div>
          <p className="text-gray-600 font-bold">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (!cidadeNormalizada) {
    return (
      <div className="min-h-screen bg-[#f9f9fb] flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-3xl shadow-lg max-w-md">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-black text-gray-800 mb-2">Cidade não identificada</h2>
          <p className="text-gray-500 mb-6">Faça login novamente.</p>
          <button onClick={handleLogout} className="bg-[#4800b2] text-white px-6 py-3 rounded-2xl font-bold">
            Fazer Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9f9fb]">
      
      {/* ============================================================================
          BARRA DE NAVEGAÇÃO SUPERIOR
      ============================================================================ */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center h-16">
            
            {/* Logo e Cidade */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/menu')}
                className="flex items-center gap-2 text-gray-600 hover:text-[#6200EE] font-bold transition-all"
              >
                <ArrowLeft size={20} />
                <span className="hidden md:inline">Voltar</span>
              </button>
              
              <div className="flex items-center gap-3">
                <div className="bg-[#6200EE] p-2 rounded-xl">
                  <Activity size={20} className="text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-black text-[#6200EE] italic tracking-tighter uppercase">
                    IBIRACO<span className="text-xs align-top text-[#e67e22]">®</span>
                  </h1>
                  <p className="text-[10px] font-bold text-gray-500 uppercase">
                    Prefeitura de {cidadeDisplay}
                  </p>
                </div>
              </div>
            </div>

            {/* Links de Navegação */}
            <nav className="hidden md:flex items-center gap-2">
              <button 
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm bg-[#6200EE] text-white"
              >
                <Construction size={18} />
                Zeladoria
              </button>
              <button 
                onClick={() => navigate('/mapa-atividade')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-gray-600 hover:bg-[#e67e22] hover:text-white transition-all"
              >
                <MapIcon size={18} />
                Mapa
              </button>
              <button 
                onClick={() => navigate('/enquetes')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-gray-600 hover:bg-[#e67e22] hover:text-white transition-all"
              >
                <Megaphone size={18} />
                Enquetes
              </button>
              <button 
                onClick={() => navigate('/condecoracoes')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-gray-600 hover:bg-[#FFD700] hover:text-[#1A1C1D] transition-all"
              >
                <Award size={18} />
                Condecorações
              </button>
            </nav>

            {/* Logout */}
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-xl font-bold text-sm hover:bg-red-100 transition-all"
            >
              <LogOut size={18} />
              <span className="hidden md:inline">Sair</span>
            </button>
          </div>
        </div>

        {/* Menu Mobile */}
        <div className="md:hidden border-t border-gray-100 bg-gray-50">
          <div className="flex flex-wrap justify-center gap-2 p-3">
            <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs bg-[#6200EE] text-white">
              <Construction size={14} /> Zeladoria
            </button>
            <button onClick={() => navigate('/mapa-atividade')} className="flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs bg-white shadow-sm">
              <MapIcon size={14} /> Mapa
            </button>
            <button onClick={() => navigate('/enquetes')} className="flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs bg-white shadow-sm">
              <Megaphone size={14} /> Enquetes
            </button>
            <button onClick={() => navigate('/condecoracoes')} className="flex items-center gap-1 px-3 py-2 rounded-lg font-bold text-xs bg-white shadow-sm">
              <Award size={14} /> Condecorações
            </button>
          </div>
        </div>
      </header>

      {/* ============================================================================
          CONTEÚDO PRINCIPAL
      ============================================================================ */}
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        
        {/* Boas-vindas */}
        <div className="mb-8">
          <h2 className="text-3xl font-black text-[#1A1C1D] mb-2">
            Zeladoria Urbana 👋
          </h2>
          <p className="text-gray-500 font-medium">
            Gerencie os reportes de {cidadeDisplay}
          </p>
        </div>

        {/* Cards de Métricas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-[2rem] p-6 shadow-sm border-l-4 border-red-500">
            <div className="flex items-center justify-between mb-2">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-gray-500 text-xs font-bold uppercase mb-1">Pendentes</p>
            <p className="text-3xl font-black text-red-600">{stats.pendentes}</p>
          </div>

          <div className="bg-white rounded-[2rem] p-6 shadow-sm border-l-4 border-yellow-500">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
            <p className="text-gray-500 text-xs font-bold uppercase mb-1">Em Obra</p>
            <p className="text-3xl font-black text-yellow-600">{stats.emObra}</p>
          </div>

          <div className="bg-white rounded-[2rem] p-6 shadow-sm border-l-4 border-green-500">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <p className="text-gray-500 text-xs font-bold uppercase mb-1">Resolvidos</p>
            <p className="text-3xl font-black text-green-600">{stats.resolvidos}</p>
          </div>

          <div className="bg-white rounded-[2rem] p-6 shadow-sm border-l-4 border-[#6200EE]">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-[#6200EE]" />
            </div>
            <p className="text-gray-500 text-xs font-bold uppercase mb-1">Taxa Resolução</p>
            <p className="text-3xl font-black text-[#6200EE]">{stats.taxaResolucao}%</p>
          </div>
        </div>

        {/* Lista de Reportes */}
        <div className="flex flex-col gap-6">
          {reportes.length > 0 ? (
            reportes.map((item) => (
              <div key={item.id} className={`bg-white rounded-[2.5rem] shadow-sm border border-black/5 p-6 flex flex-col lg:flex-row gap-8 items-center transition-all ${item.status === 'Resolvido' ? 'bg-green-50/30' : ''}`}>
                
                {/* 1. PROBLEMA (FOTO DO CIDADÃO) */}
                <div className="relative w-full lg:w-72 h-48 shrink-0">
                  <img src={item.imagemUrl} className="w-full h-full object-cover rounded-3xl" alt="Problema" />
                  <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase">
                    Problema
                  </div>
                </div>

                {/* 2. DADOS COMPLETOS */}
                <div className="flex-1 w-full space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin size={18} className="text-[#4800b2]" />
                      <h2 className="text-lg font-black text-gray-800 uppercase leading-tight">{item.endereco}</h2>
                    </div>
                    <p className="text-gray-500 text-sm italic font-medium">
                      "{item.descricao || 'O cidadão não inseriu descrição.'}"
                    </p>
                  </div>

                  {/* INFO DO USUÁRIO */}
                  <div className="flex items-center gap-2 bg-gray-50 w-fit pr-4 rounded-full border border-black/5">
                    {item.userPhoto ? (
                      <img src={item.userPhoto} className="w-8 h-8 rounded-full border-2 border-white shadow-sm" alt="User" />
                    ) : (
                      <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                        <User size={14} />
                      </div>
                    )}
                    <span className="text-[10px] font-bold text-gray-600 uppercase tracking-tight">
                      {item.userName || 'Anônimo'}
                    </span>
                  </div>

                  {/* FEEDBACK (LIKES E ESTRELAS) */}
                  <div className="flex gap-3">
                    <div className="flex items-center gap-1.5 bg-[#FFFDE7] px-3 py-1.5 rounded-full border border-[#FBC02D] shadow-sm">
                      <ThumbsUp size={14} className="text-[#FBC02D]" />
                      <span className="text-xs font-black text-[#FBC02D]">{item.likes || 0}</span>
                    </div>
                    {item.rating && (
                      <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-full border border-[#4800b2] shadow-sm">
                        <Star size={14} className="text-[#4800b2] fill-[#4800b2]" />
                        <span className="text-xs font-black text-[#4800b2]">{item.rating} Estrelas</span>
                      </div>
                    )}
                  </div>

                  {/* BOTÕES DE STATUS */}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <button 
                      onClick={() => alterarStatus(item.id, 'Pendente')} 
                      className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase transition-all shadow-sm ${
                        item.status === 'Pendente' 
                          ? 'bg-red-600 text-white scale-105' 
                          : 'bg-white text-gray-400 border border-gray-200'
                      }`}
                    >
                      Pendente
                    </button>
                    <button 
                      onClick={() => alterarStatus(item.id, 'Em obra')} 
                      className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase transition-all shadow-sm ${
                        item.status === 'Em obra' 
                          ? 'bg-[#6200EE] text-white scale-105' 
                          : 'bg-white text-gray-400 border border-gray-200'
                      }`}
                    >
                      Em Obra
                    </button>
                    <button 
                      onClick={() => alterarStatus(item.id, 'Resolvido')} 
                      className={`px-5 py-2.5 rounded-2xl text-[10px] font-black uppercase transition-all shadow-sm ${
                        item.status === 'Resolvido' 
                          ? 'bg-green-600 text-white scale-105' 
                          : 'bg-white text-gray-400 border border-gray-200'
                      }`}
                    >
                      Resolvido
                    </button>
                  </div>
                </div>

                {/* 3. SOLUÇÃO (FOTO DA PREFEITURA) */}
                <div className="w-full lg:w-80 h-48 border-2 border-dashed border-gray-200 rounded-3xl flex flex-col items-center justify-center relative overflow-hidden bg-gray-50 group">
                  {item.imagemSolucao ? (
                    <div className="w-full h-full">
                      <img src={item.imagemSolucao} className="w-full h-full object-cover" alt="Solução" />
                      <label className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                        <Camera className="text-white" />
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*" 
                          onChange={(e) => e.target.files && uploadResolvido(item.id, e.target.files[0])} 
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="cursor-pointer flex flex-col items-center gap-2 p-6 text-center">
                      {uploadingId === item.id ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4800b2]"></div>
                      ) : (
                        <>
                          <div className="w-12 h-12 bg-[#4800b2]/10 rounded-full flex items-center justify-center mb-1">
                            <Camera size={24} className="text-[#4800b2]" />
                          </div>
                          <span className="text-[10px] font-black text-[#4800b2] uppercase tracking-tighter">
                            Postar Foto do Resolvido
                          </span>
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*" 
                            onChange={(e) => e.target.files && uploadResolvido(item.id, e.target.files[0])} 
                          />
                        </>
                      )}
                    </label>
                  )}
                  <div className="absolute top-2 right-2 bg-green-600 text-white text-[10px] font-black px-3 py-1 rounded-full uppercase shadow-md">
                    Solução
                  </div>
                </div>

              </div>
            ))
          ) : (
            <div className="bg-white rounded-[3rem] p-12 text-center shadow-sm">
              <Construction className="w-20 h-20 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-black text-gray-800 mb-2">Nenhum reporte encontrado</h3>
              <p className="text-gray-500">Ainda não há reportes em {cidadeDisplay}.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}